import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { GoogleOAuthProvider, type CredentialResponse } from '@react-oauth/google';
import { adminClient } from '@/lib/adminClient';
import { AuthContext, type AdminUser } from '@/auth/context';
import { getErrorMessage } from '@/errors/messages';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

// A stored session is the only thing the provider waits on at start.
function hasStoredSession(): boolean {
  return Boolean(localStorage.getItem('admin_google_credential') && localStorage.getItem('admin_user'));
}

function AuthProviderInner({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(hasStoredSession);
  const [error, setError] = useState<string | null>(null);

  // Restore session from localStorage on mount.
  useEffect(() => {
    const credential = localStorage.getItem('admin_google_credential');
    const storedUser = localStorage.getItem('admin_user');
    if (!credential || !storedUser) return;
    adminClient.setCredential(credential);
    adminClient.getMe()
      .then((me) => {
        setUser({ email: me.email });
      })
      .catch(() => {
        localStorage.removeItem('admin_google_credential');
        localStorage.removeItem('admin_user');
        adminClient.clearCredential();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleGoogleSuccess = useCallback(async (response: CredentialResponse) => {
    if (!response.credential) {
      setError('No credential received from Google');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      adminClient.setCredential(response.credential);
      const me = await adminClient.getMe();

      if (!me.email.endsWith('@liminal.cash')) {
        throw new Error('Only @liminal.cash accounts are allowed.');
      }

      const adminUser: AdminUser = { email: me.email };
      localStorage.setItem('admin_google_credential', response.credential);
      localStorage.setItem('admin_user', JSON.stringify(adminUser));
      setUser(adminUser);
    } catch (err) {
      adminClient.clearCredential();
      localStorage.removeItem('admin_google_credential');
      localStorage.removeItem('admin_user');
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleGoogleError = useCallback(() => {
    setError('Google sign-in failed. Please try again.');
  }, []);

  const logout = useCallback(() => {
    adminClient.clearCredential();
    localStorage.removeItem('admin_google_credential');
    localStorage.removeItem('admin_user');
    setUser(null);
    setError(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, isAuthenticated: !!user, handleGoogleSuccess, handleGoogleError, logout, error }}>
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (!GOOGLE_CLIENT_ID) {
    console.error('[admin] VITE_GOOGLE_CLIENT_ID is not set! Add it to application/web/admin/.env.local');
  }
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </GoogleOAuthProvider>
  );
}

